import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetMonthlyMetricsSummaryQuery } from '../../pages/metricdashboard/metricDashboardSlice';
import './MetricsScoreCard.scss';
import gears from '../../assets/Gears.png';
import { selectCurrentRole } from '../../pages/login/authSlice';
import { AGENTROLE, SUPERVISORROLE } from '../../constants';
import { ConstScorecardRole } from '../../utils/typeConstants';
import { MetricMonthlySummaryCard } from '../metricMonthlySummaryCard/MetricMonthlySummaryCard';
import { GradientDefs } from '../metricMonthlySummaryCard/GradientDefs';
import { isTieredScorecard } from '../../utils/metricAttainment';
import { MetricCardsSkeletonLoader } from '../metricCardsSkeletonLoader/MetricCardsSkeletonLoader';
import standardMetricsIcon from '../../assets/standardMetrics.png';
import additionalMetricsIcon from '../../assets/additionalMetrics.png';

interface MetricsScoreCardProps {
    agentnumber: any;
    scorecardVersion: number;
    inputmonth: number;
    inputyear: number;
    // isPayoutMetric?: boolean; 
}

export const MetricsScoreCard: React.FC<MetricsScoreCardProps> = ({
    agentnumber,
    scorecardVersion,
    inputmonth,
    inputyear,
    // isPayoutMetric = true,
}) => {
    const role = useSelector(selectCurrentRole);
    const [scorecardRoleId, setScorecardRoleId] = useState<number>(0);
    // const [monthlyMetrics, setMonthlyMetrics] = useState<any>([]);
    const [activeTab, setActiveTab] = useState<'standard' | 'additional'>('standard');

    useEffect(() => {
        if (role === AGENTROLE) {
            setScorecardRoleId(ConstScorecardRole.ASSOCIATE);
        } else if (role === SUPERVISORROLE) {
            setScorecardRoleId(ConstScorecardRole.TEAM_LEAD);
        } else {
            setScorecardRoleId(0);
        }
    }, [role]);

    const { data: monthlyMetricsData, isFetching: isMetricsFetching } = useGetMonthlyMetricsSummaryQuery(
        {
            employeenumber: parseInt(agentnumber, 10),
            year: inputyear,
            month: inputmonth,
            scorecardVersionId: scorecardVersion ?? 0,
            scorecardRoleId,
            // isPayoutMetric,
        },
        {
            skip: !agentnumber || !inputyear || !inputmonth,
        }
    );

    // useEffect(() => {
    //     if (monthlyMetricsData?.data?.length > 0) {
    //         setMonthlyMetrics(monthlyMetricsData);
    //     } else setMonthlyMetrics([]);
    // }, [monthlyMetricsData]);

    const metrics = monthlyMetricsData?.data || [];

    const standardMetrics = metrics.filter((m: any) => m.is_payout_metric === true);
    const additionalMetrics = metrics.filter((m: any) => m.is_payout_metric === false);
    const displayedMetrics = activeTab === 'standard' ? standardMetrics : additionalMetrics;

    // Precomputed (not a nested ternary in JSX) - matches this codebase's if/else style.
    let cardsRowContent: React.ReactNode;
    if (isMetricsFetching) {
        cardsRowContent = <MetricCardsSkeletonLoader />;
    } else if (displayedMetrics.length > 0) {
        cardsRowContent = displayedMetrics
            // ?.filter((metric: any) => metric.metric_direction_id === 1)
            .map((metric: any, i: number) => {
                const variant = isTieredScorecard(metric?.scorecard_calculation_rule_id)
                    ? 'tiered'
                    : 'legacy';
                return (
                    <div
                        key={metric.metric_id ?? i}
                        style={{
                            flex: '0 0 calc((100% - 4rem) / 5)',
                            minWidth: '180px',
                        }}
                    >
                        <MetricMonthlySummaryCard
                            index={i}
                            metricNameVal={metric.label}
                            targetVal={metric.target}
                            metricTypeName={metric.metric_type_name}
                            metricVal={metric.metric_value}
                            metricAttained={metric.metric_attained}
                            metricDirectionId={metric.metric_direction_id}
                            metricOmitted={metric.metric_omitted}
                            metricDesc={metric.metric_desc}
                            variant={variant}
                            attainmentRank={metric.attainment_rank}
                            attainmentLevelLabel={metric.metric_attainment_level_label}
                            targets={metric.targets}
                            min={metric.min}
                            max={metric.max}
                        />
                    </div>
                );
            });
    } else {
        cardsRowContent = (
            <div className="no-data-wrapper">
                <div className="no-data-inner">
                    <img src={gears} alt="No Data" className="gears-img" />
                    <div className="no-data-text">
                        No Data
                        <br />
                        Available
                    </div>
                </div>
            </div>
        );
    }



    return (
        <div className="metrics-score-card">
            <GradientDefs />
            {additionalMetrics.length > 0 && (
                <div className="metrics-tab-switcher mt-3">
                    <button
                        className={`metrics-tab ${activeTab === 'standard' ? 'metrics-tab--active' : ''}`}
                        onClick={() => setActiveTab('standard')}
                    >
                        <img src={standardMetricsIcon} alt="Standard Metrics" style={{ width: '16px', height: '16px' }} />
                        Standard Metrics
                    </button>

                    <button
                        className={`metrics-tab ${activeTab === 'additional' ? 'metrics-tab--active' : ''}`}
                        onClick={() => setActiveTab('additional')}
                    >
                        <img src={additionalMetricsIcon} alt="Additional Metrics" style={{ width: '16px', height: '16px' }} />
                        Additional Metrics (Non Payout)
                    </button>
                </div>
            )}
                <div className="metrics-scroll-wrapper">
                <div className="metrics-card-row">
                    {cardsRowContent}
                </div>
            </div>
        </div>
    );
};
